import React from "react";
import { useTranslation } from "react-i18next";

import { Box, Stack, Text, Divider as DividerBase } from "@mantine/core";

import type {
  AccountActivateFormData,
  AccountActivateForm,
} from "src/services/accountActivation/accountActivationTypes";

// parts of the form
import { SetPassword } from "./formComponents/SetPassword/SetPassword";
import { NotificationPreferences } from "./formComponents/NotificationPreferences";
import { TermsOfService } from "./formComponents/TermsOfService";
import { McsConsentToTreatInput } from "./formComponents/mcs-consent-to-treat";

import { LanguageSelectInput } from "@components/LanguageSelect/index";

import { CompleteSetupButton } from "@components/CompleteSetupButton";

// types
import type { StartPageConfig } from "src/services/accountActivation/startPageConfig";
// styles
import cx from "./account_activate_form.module.css";

const Divider = DividerBase.withProps({ className: cx.divider });
/**
 * VIEW
 */

interface AccountActivateViewProps {
  form: AccountActivateForm;
  startPageConfig: StartPageConfig;
  handleSubmit: (values: AccountActivateFormData) => void;
  handleValidationErrors: (errors: Record<string, unknown>) => void;
  isMutationLoading: boolean;
}

export const AccountActivateFormUI: React.FC<AccountActivateViewProps> = (
  props,
) => {
  const { form, handleSubmit, handleValidationErrors } = props;

  const { t } = useTranslation();

  const showNotifications =
    props.startPageConfig.allowUpdateNotificationPreferences;

  const showMcsConsentToTreat = props.startPageConfig.showMcsConsentToTreat;

  const showTerms = props.startPageConfig.showTermsConditions;

  return (
    <form
      noValidate
      onSubmit={form.onSubmit(handleSubmit, handleValidationErrors)}
      style={{ flex: 1, display: "flex" }}
    >
      <Stack w="100%">
        {/* always able to set password here */}
        <SetPassword form={form} />

        <Divider />

        <LanguageSelectInput
          {...form.getInputProps("preferredLanguage")}
          label={t("startPage.inputs.selectLanguage")}
        />

        <Divider />

        <Stack>
          {showNotifications ? <NotificationPreferences form={form} /> : null}
          {showTerms ? <TermsOfService form={form} /> : null}
          {showMcsConsentToTreat ? (
            <McsConsentToTreatInput form={form} />
          ) : null}
        </Stack>

        <Box
          style={{
            display: "flex",
            alignItems: "center",
            alignSelf: "center",
          }}
        >
          <CompleteSetupButton
            isLoading={props.isMutationLoading}
            type="submit"
            className={cx["complete-setup-button"]}
            data-testid="complete-setup-button"
          >
            <Text className={cx["complete-setup-button-text"]}>
              {t("startPage.completeSetupButton")}
            </Text>
          </CompleteSetupButton>
        </Box>
      </Stack>
    </form>
  );
};
